import { useEffect, useLayoutEffect, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Filter, READ_QUESTIONS_MAX } from 'cat-shared/utils'
import { AppSegment } from 'client/ui-building-blocks'
import { QuestionsList } from 'client/components'
import { useGetQuestions } from 'client/api'
import { BrowseQuestionsFormValues } from 'client/utils'
import { useAppContext, usePageStateContext } from 'client/hooks'
import { displaySnackBar, isAccountFull, getAccountStatus } from 'client/services'

import SearchForm from './SearchForm/search-form'

const BrowseQuestionsPage = () => {
  const { t } = useTranslation()

  const { authUser } = useAppContext()
  const { setBrowseQuestionsPageState, browseQuestionsPageState } = usePageStateContext()

  const { filter, category, search, pageNo } = browseQuestionsPageState

  const accountStatus = getAccountStatus(authUser)

  const form = useForm<BrowseQuestionsFormValues>({
    mode: 'onChange',
    defaultValues: {
      filter: Filter.Answered,
      category: 'any',
      search: '',
    },
  })

  const disableSearchForm = !isAccountFull(accountStatus)

  useEffect(() => {
    form.reset({ filter, category, search }, { keepDefaultValues: true })
    return () => {
      setBrowseQuestionsPageState(state => ({ ...state, pageNo: 1 }))
    }
  }, [])

  const { data, refetch, isLoading, isFetching } = useGetQuestions({
    onSuccess: data => {
      form.reset({ filter, category, search }, { keepDefaultValues: true })
      if (!data?.data?.length) {
        setBrowseQuestionsPageState(state => ({ ...state, pageNo: 1 }))
      }
    },
    onError: err => {
      if (err?.msg) {
        displaySnackBar(err.msg)
      }
    },
  })

  useLayoutEffect(() => {
    if (!disableSearchForm) {
      refetch()
    }
  }, [refetch, disableSearchForm])

  const onSubmitClick = () => {
    setBrowseQuestionsPageState(state => ({ ...state, pageNo: 1 }))
    refetch()
  }

  const onPageChange = (pageNo: number) => {
    if (!isLoading && !isFetching) {
      setBrowseQuestionsPageState(state => ({ ...state, pageNo }))
      setTimeout(refetch)
    }
  }

  const first = (pageNo - 1) * READ_QUESTIONS_MAX + 1
  const last = (pageNo - 1) * READ_QUESTIONS_MAX + data?.data.length

  const title = useMemo(() => {
    return isLoading || isFetching
      ? t('browseQuestionsPage.list.pageNo', { pageNo })
      : data?.data.length <= 1
        ? t('browseQuestionsPage.list.totalOnePage', { pageNo, total: data?.count })
        : t('browseQuestionsPage.list.total', { pageNo, total: data?.count, first, last })
  }, [isLoading, isFetching])

  return (
    <FormProvider {...form}>
      <AppSegment
        headerProps={{
          text: t('browseQuestionsPage.header'),
          iconProps: { name: 'SEARCH' },
        }}
        marginBottom="medium"
      >
        <SearchForm
          activePage={pageNo}
          totalPages={Math.ceil(data?.count / READ_QUESTIONS_MAX) || 1}
          isLoading={isFetching}
          onPageChange={onPageChange}
          onSubmitClick={onSubmitClick}
          disabled={disableSearchForm}
        />
      </AppSegment>
      <QuestionsList
        questionsData={data}
        title={title}
        isLoading={false}
        isFetching={isFetching}
        pageSize={READ_QUESTIONS_MAX}
        activePage={pageNo}
      />
    </FormProvider>
  )
}

export default BrowseQuestionsPage
